import { Controller } from '@hotwired/stimulus'
import { initLocationMap, renderMapLocations, updateMapLocations, recenterMapToLocations } from '../utils/map'
import { get } from "@rails/request.js";
import log from '../utils/log'

const PAGE_SIZE = 25;
const DEFAULT_MAP_ZOOM = 9;

export default class extends Controller {
  static targets = ['showAssetToggler', 'sideBar', 'locationList', 'resultsCount'];
  static values = { targetId: String };

  pageNumber = 1;
  totalPages = 1;
  searchParams = {};
  pinsList = [];
  loading = false;
  requestSeq = 0;
  lastAppliedSearch = "";
  omitBounds = false;

  async connect () {
    this.map = initLocationMap(this.targetIdValue, window.mapbox_data.coordinates, DEFAULT_MAP_ZOOM, true)
    await this.renderMap()

    if (typeof window.__mapUpdating === 'undefined'){
      window.__mapUpdating = false;
    }
    if (typeof window.__mapRecentering === 'undefined'){
      window.__mapRecentering = false;
    }

    this.map.on('moveend', () => {
      if (window.__mapUpdating || window.__mapRecentering) {
        return;
      }
      this.updateMap();
    });

    this.debouncedScroll = this.debounce(this.scrolled.bind(this), 200);
    this.sideBarTarget.addEventListener('scroll', this.debouncedScroll);
    }

 disconnect() {
    if (this.debouncedScroll) {
      this.sideBarTarget.removeEventListener('scroll', this.debouncedScroll);
    }
  }

  bounds() {
    const {
      _ne: { lat: latitude_1, lng: longitude_1 },
      _sw: { lat: latitude_2, lng: longitude_2 }
    } = this.map.getBounds();

    return {
      coordinates: true,
      latitude_1,
      longitude_1,
      latitude_2,
      longitude_2,
    };
  }

  hasMore() {
    return this.pageNumber < this.totalPages
  }

  currentSearch() {
    const search = (window.searchParams ?? {})["search_attributes"]
    return typeof search === "string" ? search.trim() : ""
  }

  reset() {
    if (this.hasShowAssetTogglerTarget) {
      this.showAssetTogglerTarget.checked = false;
    }

    // jumpTo fires moveend synchronously; suppress the listener so updateMap runs once
    window.__mapRecentering = true;
    try {
      this.map.jumpTo({ center: window.mapbox_data.coordinates, zoom: DEFAULT_MAP_ZOOM });
    } finally {
      window.__mapRecentering = false;
    }

    this.updateMap();
  }

  async scrolled() {
    if (this.loading || !this.hasMore()) return;

    const { bottom } = this.locationListTarget.getBoundingClientRect();

    if (this.sideBarTarget.clientHeight + this.sideBarTarget.offsetTop > bottom ) {
      const seq = this.requestSeq;
      this.loading = true;
      this.showLoadingMore();
      try {
        const res = await this.fetchCardsPage(this.pageNumber + 1);
        if (seq !== this.requestSeq || !res) return;

        this.pageNumber = res.meta?.current_page ?? this.pageNumber + 1;
        this.totalPages = res.meta?.total_pages ?? this.pageNumber;
        this.loadLocations(res.locations, true);
      } finally {
        this.hideLoadingMore();
        this.loading = false;
      }
    }
  }

  updateResultsCount(count) {
    if (this.hasResultsCountTarget) {
      this.resultsCountTarget.textContent = `${count} result${count !== 1 ? 's' : ''}`;
    }
  }

  async renderMap() {
    const data = await this.fetchMapData(++this.requestSeq);
    if (!data) return;

    this.loadLocations(data.cards);
    renderMapLocations(this.map, this.pinsList);
  }

  async updateMap() {
    if (window.__mapUpdating) {
      // don't drop an update that raced an in-flight one; run it after
      this.queuedUpdate = true;
      return;
    }

    window.__mapUpdating = true;
    try {
      this.searchParams = window.searchParams ?? {};

      // a newly applied search fetches globally (no bounds) and recenters once;
      // map moves while that search stays active narrow the results by viewport
      const search = this.currentSearch();
      const freshSearch = search !== "" && search !== this.lastAppliedSearch;
      this.omitBounds = freshSearch;

      const data = await this.fetchMapData(++this.requestSeq);
      if (!data) return;

      this.lastAppliedSearch = search;
      this.loadLocations(data.cards);
      updateMapLocations(this.map, this.pinsList);

      if (freshSearch && this.pinsList.length > 0) {
        window.__mapRecentering = true;
        recenterMapToLocations(this.map, this.pinsList);
        this.map.once('moveend', () => {
          window.__mapRecentering = false;
        });
      }

    } finally {
      window.__mapUpdating = false;
      if (this.queuedUpdate) {
        this.queuedUpdate = false;
        this.updateMap();
      }
    }
  }

  // fetches pins and the first card page together and reconciles them; falls back
  // to the cards payload for pins when the backend has no pins endpoint yet
  async fetchMapData(seq) {
    const [pins, cards] = await Promise.all([this.fetchPins(), this.fetchCardsPage(1)]);
    if (seq !== this.requestSeq) return null;

    if (!cards) {
      this.showLoadError();
      return null;
    }

    this.pageNumber = cards.meta?.current_page ?? 1;
    this.totalPages = cards.meta?.total_pages ?? 1;
    this.pinsList = pins?.locations ?? cards.locations;
    this.updateResultsCount(pins?.meta?.total_count ?? cards.meta?.total_count ?? cards.locations.length);

    return { cards: cards.locations };
  }

  loadLocations(locations, append=false) {
    if (!append) {
      this.locationListTarget.innerHTML = ""

      if (locations.length === 0) {
        this.locationListTarget.innerHTML = '<h3 class="p-2">No locations found</h3>'
        return
      }
    }

    const showAsset = this.hasShowAssetTogglerTarget ? this.showAssetTogglerTarget.checked : false;

    locations.forEach( location => {
      const cardElement = this.createLocationCard(location, showAsset)
      this.locationListTarget.appendChild(cardElement)
    })
  }

  showLoadError() {
    this.locationListTarget.innerHTML = '<h3 class="p-2">Locations could not be loaded. Please try again.</h3>'
    this.updateResultsCount(0)
  }

  showLoadingMore() {
    if (this.locationListTarget.querySelector('.load-more-indicator')) return;

    const indicator = document.createElement('div')
    indicator.className = 'load-more-indicator p-2 text-xs'
    indicator.textContent = 'Loading more…'
    this.locationListTarget.appendChild(indicator)
  }

  hideLoadingMore() {
    this.locationListTarget.querySelector('.load-more-indicator')?.remove()
  }

  createLocationCard(location, showAsset) {
    const cardElement = document.createElement('div')
    cardElement.setAttribute('data-controller', 'location-card')
    cardElement.setAttribute('data-location-card-location-value', JSON.stringify(location))
    cardElement.setAttribute('data-location-card-show-asset-value', showAsset)
    return cardElement
  }

  updateShowAssets() {
    const showAsset = this.showAssetTogglerTarget.checked
    const locationCards = this.locationListTarget.querySelectorAll('[data-controller*="location-card"]')
    locationCards.forEach(card => {
      const controller = this.application.getControllerForElementAndIdentifier(card, 'location-card')
      if (controller) {
        controller.showAssetValue = showAsset
      }
    })
  }

  queryParams() {
    const params = { ...this.searchParams }

    if (!this.omitBounds) {
      Object.assign(params, this.bounds())
    }

    return params
  }

  async fetchPins() {
    try {
      const response = await get("/location_management/api/v1/locations/pins", {
        query: this.queryParams()
      });
      if (!response.ok) return null;

      return await response.json;
    } catch (e) {
      log.debug(e)
      return null;
    }
  }

  async fetchCardsPage(page) {
    try {
      const response = await get("/location_management/api/v1/locations", {
        query: { ...this.queryParams(), page: page, per_page: PAGE_SIZE }
      });
      if (!response.ok) return null;

      return await response.json;
    } catch (e) {
      log.debug(e)
      return null;
    }
  }

  debounce(fn, delay) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => fn(...args), delay)
    }
  }
}
